import { Text, Title, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconStarFilled } from '@tabler/icons-react';
import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Icon, IconName } from 'shared/components/Icon';
import { useIsMobile } from 'shared/hooks/utils/use-is-mobile';
import { Dropdown, Input } from 'shared/io';
import { useGetViewsByResource } from 'shared/utils/selectors';
import type { ResourceType } from 'types/resource-types';
import type { TableView } from 'types/views';

import { ViewSettingsModal } from '../ViewSettings';
import styleIdentifiers from './CustomTableTitle.module.scss';
import { orderViewsFavoriteFirst } from './order-views';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  title: string;
  actions: React.ReactNode;
  tableName: ResourceType;
  submit: Function;
  invalidateViews: () => void;
};

export default function CustomTableTitle({
  title,
  actions,
  tableName,
  submit,
  invalidateViews,
}: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const theme = useMantineTheme();

  const tableViews = useGetViewsByResource()[tableName];

  const [currentViewId, setCurrentViewId] = useState();
  const [currentTabIndex, _setCurrentTabIndex] = useState(0);
  const [searchedView, setSearchedView] = useState('');

  const orderedViews = orderViewsFavoriteFirst(tableViews);

  useEffect(() => {
    if (location.hash !== currentViewId) setCurrentViewId(location.hash.slice(1) as any);
  }, [location.hash]);

  const openViewModal = () => {
    modals.open({
      title: t(i18nKeys.VIEWS.SETTINGS.TITLE),
      size: '800px',
      centered: true,
      onClose: invalidateViews,
      children: <ViewSettingsModal resourceType={tableName! as ResourceType} />,
    });
  };

  const filtersToUrl = (view: any) => {
    const newFilters = {};
    view.filter_fields.forEach((filter: any) => {
      if (
        filter.applied_filter.operator === 'null' ||
        filter.applied_filter.operator === 'not_null'
      ) {
        // @ts-expect-error implicit any in object indexing
        newFilters[`${filter.name}_handler`] = filter.applied_filter.operator;
      } else {
        if (filter.filter_type === 'date') {
          // @ts-expect-error implicit any in object indexing
          newFilters[`${filter.name}_start`] = filter.applied_filter.value[0];
          // @ts-expect-error implicit any in object indexing
          newFilters[`${filter.name}_end`] = filter.applied_filter.value[1];
          // @ts-expect-error implicit any in object indexing
          delete newFilters[filter.name];
        } else if (filter.filter_type === 'number') {
          // @ts-expect-error implicit any in object indexing
          newFilters[`min_${filter.name}`] = filter.applied_filter.value[0];
          // @ts-expect-error implicit any in object indexing
          newFilters[`max_${filter.name}`] = filter.applied_filter.value[1];
          // @ts-expect-error implicit any in object indexing
          delete newFilters[filter.name];
        } else {
          // @ts-expect-error implicit any in object indexing
          newFilters[filter.name] = filter.applied_filter.value;
        }
        if (filter.applied_filter.operator) {
          // @ts-expect-error
          newFilters[`${filter.name}_operator`] = filter.applied_filter.operator;
        }
      }
    });
    return newFilters;
  };

  const setQuickSearchValue = (value: any) => {
    const input = document.querySelector("[data-quick-search='1']") as any;
    if (input) {
      input.value = value;
    }
  };

  const changeView = (view: TableView) => () => {
    const urlFilters = Object.entries({
      ...filtersToUrl(view),
    });
    history.push(
      `${location.pathname}${urlFilters.reduce(
        (acc, [key, value]) => `${acc}${key}=${value}&`,
        '?',
      )}${`#${view.id}`}`,
    );
    let quickSearchValue = '';
    urlFilters.forEach(filter => {
      if (filter[0] === 'quick_search') {
        quickSearchValue = filter[1] as string;
      }
    });
    setQuickSearchValue(quickSearchValue);
  };

  const setCurrentTabIndex = (nbre: any) => () => {
    _setCurrentTabIndex(currentTabIndex + nbre);
  };

  const filteredViews = (views: any) =>
    searchedView
      ? views.filter(
          (view: any) => view.name.toLowerCase().indexOf(searchedView.toLowerCase()) !== -1,
        )
      : views;

  const optionsMenu = () => (
    <div className={styles('options')}>
      <Dropdown toggleContent selectorContent={<Icon name={IconName.MINIMAL_DOWN} />}>
        <div className={styles('dropdown-input')}>
          <Input
            name="searched_view"
            autoComplete="off"
            placeholder={t(i18nKeys.FORM.SEARCH)}
            noMargin
            onValueChanged={setSearchedView}
            onClick={e => {
              e.stopPropagation();
            }}
          />
        </div>
        {filteredViews(tableViews).map((view: any) => (
          <div key={view.id} className={styles('dropdown-item')} onClick={changeView(view)}>
            {view.name}
          </div>
        ))}
      </Dropdown>
      <Icon name={IconName.SETTINGS_GEAR} onClick={openViewModal} />
    </div>
  );

  return (
    <div className={styles('custom-table-title', tableName && 'table-view')}>
      <div className={styles('tab-container')}>
        <div className={styles('title')}>
          <Title mr="space3" mt="space1" order={4}>
            {title}
          </Title>
          {tableViews && isMobile && optionsMenu()}
        </div>
        {tableViews && (
          <>
            <div className={styles('tab-wrapper')}>
              {currentTabIndex > 0 && (
                <div className={styles('tab-item')} onClick={setCurrentTabIndex(-1)}>
                  <Icon name={IconName.MINIMAL_LEFT} />
                </div>
              )}
              {orderedViews.slice(currentTabIndex, currentTabIndex + 3).map(view => (
                <div
                  key={view.id}
                  className={styles('tab-item', view.id === currentViewId && 'active')}
                  onClick={changeView(view)}
                >
                  {view.is_favorite && <IconStarFilled size={20} color={theme.colors.gray[6]} />}
                  <Text ml={view.is_favorite ? 8 : 0}>{view.name}</Text>
                </div>
              ))}
              {currentTabIndex < tableViews.length - 3 && (
                <div className={styles('tab-item')} onClick={setCurrentTabIndex(1)}>
                  <Icon name={IconName.MINIMAL_RIGHT} />
                </div>
              )}
              <div className={styles('border-mobile')} />
            </div>
            {!isMobile && optionsMenu()}
          </>
        )}
      </div>
      <div className={styles('actions-container', isMobile && 'space-between')}>
        {isMobile && (
          <div className={styles('quick-search')}>
            <Input
              noMargin
              quickSearch
              shadow
              type="text"
              placeholder={t(i18nKeys.FORM.QUICK_SEARCH.PLACEHOLDER)}
              onValueChanged={submit}
            />
          </div>
        )}
        <div className={styles('actions')}>{actions}</div>
      </div>
    </div>
  );
}
